import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { FaSearch, FaFilter } from "react-icons/fa";
import Navbar from "../components/NavBar";
import { useAppDispatch, useAppSelector } from "../store/store";
import PostAction from "../store/post/action";
import AppAction from "../store/app/action";
import FavoritesActions from "../store/favorites/action";
import LoadingView from "../components/LoadingView";
import ProductCard from "../components/ProductCard";
import Slideshow from "../components/Slideshow";
import ClothesFiltersModal, { defaultFilters, ClothesFiltersState } from "../components/ClothesFiltersModal";
import { categoryLabels, categoryList } from "../utils/categoryData";
import { ClothesTypes } from "../models/clothes/clothes.model";
import Clothes from "../models/clothes/clothes.model";
import AuthAction from "../store/auth/action";
import AppStoreLinks from "../components/AppStoreLinks";
import MobileAppDownloadBar from "../components/MobileAppDownloadBar";

const POSTS_PER_PAGE = 30;

const CatalogHome = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { user } = useAppSelector((s) => s.auth);
    const { data: appData } = useAppSelector((s) => s.app);
    const { isLoading, postsData, postDataLastRequest, searchPostsData, filterPostsData, error } = useAppSelector((s) => s.post);
    const { favorites } = useAppSelector((s) => s.favorites);

    const [searchInput, setSearchInput] = useState("");
    const [appliedSearchName, setAppliedSearchName] = useState("");
    const perPage = appData?.posts_per_page?.home ?? POSTS_PER_PAGE;
    const runSearch = () => {
        const name = searchInput.trim();
        setAppliedSearchName(name);
        if (!name && !hasActiveFilters) return;
        if (hasActiveFilters) {
            PostAction.getPostsByFilter(perPage, filters, name, dispatch);
        } else {
            PostAction.getPostsByName(perPage, name, dispatch);
        }
    };
    const [filters, setFilters] = useState<ClothesFiltersState>(defaultFilters);
    const [filtersModalOpen, setFiltersModalOpen] = useState(false);
    const [mobileAppBarOpen, setMobileAppBarOpen] = useState(false);

    const applyFiltersAndFetch = (newFilters: ClothesFiltersState) => {
        setFilters(newFilters);
        const per = appData?.posts_per_page?.home ?? POSTS_PER_PAGE;
        PostAction.getPostsByFilter(per, newFilters, appliedSearchName, dispatch);
    };

    const today = moment().format("DD-MM-YYYY");

    const hasActiveFilters =
        filters.category !== null ||
        filters.gender !== null ||
        filters.size !== null ||
        filters.colors.length > 0 ||
        filters.minSellPrice !== "0" ||
        filters.maxSellPrice !== "500" ||
        filters.minRentPrice !== "0" ||
        filters.maxRentPrice !== "100";

    const displayList = hasActiveFilters ? filterPostsData : (appliedSearchName ? searchPostsData : postsData ?? []);

    useEffect(() => {
        AppAction.getAppData(dispatch);
    }, [dispatch]);

    useEffect(() => {
        const perPage = appData?.posts_per_page?.home ?? POSTS_PER_PAGE;
        if (postDataLastRequest === null || postsData.length === 0) {
            PostAction.getPosts(perPage, dispatch);
        } else if (postDataLastRequest.substring(0, 10) !== today) {
            PostAction.getPosts(perPage, dispatch);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onCategoryClick = (type: ClothesTypes) => {
        const perPage = appData?.posts_per_page?.other ?? 50;
        PostAction.getPostsByCategory(perPage, type, dispatch);
        navigate("/category/" + type);
    };

    const onItemClick = (item: Clothes) => {
        navigate("/item", { state: { item } });
    };

    const onToggleFavorite = (item: Clothes, e: React.MouseEvent) => {
        e.stopPropagation();
        if (FavoritesActions.isFavorite(item, favorites)) {
            FavoritesActions.removeItem(item, favorites, dispatch);
        } else {
            FavoritesActions.addItem(item, favorites, dispatch);
        }
    };

    const checkSubscriptionValidation = () => {
        if (user?.subscription) {
            const expirationTime = moment(user.subscription.valid_until, "DD-MM-YYYY").hour(23).minute(59).second(59);
            if (moment().valueOf() > expirationTime.valueOf()) {
                AuthAction.updateUserSubscription(user, null, dispatch);
            };
        }
    }

    const checkUserVerification = (verified: boolean) => {
        if (!verified) {
            AuthAction.updateUserVerification(user!, true, dispatch);
        }
    }

    useEffect(() => {
        if (user) {
            checkSubscriptionValidation();
            // checkUserVerification(user.verified);
        }
    }, [user]);

    return (
        <div
            className={`min-h-screen bg-[#f8f2ed] font-manrope${mobileAppBarOpen ? " pb-32 md:pb-0" : ""}`}
        >
            <Navbar />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 lg:py-8">
                {/* Header */}
                <header className="mb-6 lg:mb-8">
                    <h1 className="text-3xl lg:text-4xl font-bold text-[#704e37] tracking-tight">
                        {user ? `Përshëndetje, ${user.first_name}` : "Ballina"}
                    </h1>
                    <p className="mt-1 text-gray-600 mb-4">
                        Zgjidhni nga katalogu ose kërkoni veshje.
                    </p>

                    <AppStoreLinks className="mb-5" />

                    <Slideshow />

                    <div className="mt-5 flex flex-col sm:flex-row gap-3 max-w-2xl">
                        <div className="flex-1 flex min-w-0 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden focus-within:ring-2 focus-within:ring-[#704e37]/40 focus-within:border-[#704e37]">
                            <div className="relative flex-1 min-w-0">
                                <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
                                <input
                                    type="text"
                                    placeholder="Kërko sipas emrit..."
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && runSearch()}
                                    className="w-full pl-12 pr-4 py-3 bg-transparent focus:outline-none placeholder:text-gray-400"
                                />
                            </div>
                            <button
                                type="button"
                                onClick={runSearch}
                                className="shrink-0 border-l border-gray-200 px-5 py-3 bg-[#704e37] text-white font-medium hover:bg-[#704e37]/90 transition"
                            >
                                Kërko
                            </button>
                        </div>
                        <button
                            type="button"
                            onClick={() => setFiltersModalOpen(true)}
                            className={`flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-medium transition shadow-sm ${hasActiveFilters ? "bg-[#704e37] text-white" : "bg-white border border-[#ebcaaa] text-[#704e37] hover:bg-[#704e37] hover:text-white hover:border-[#704e37]"}`}
                        >
                            <FaFilter />
                            Filtro
                        </button>
                    </div>
                </header>

                <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
                    {/* Left: Categories column */}
                    <aside className="lg:w-52 shrink-0">
                        <nav className="lg:sticky lg:top-24">
                            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 px-1">
                                Kategoritë
                            </h2>
                            <ul className="flex lg:flex-col gap-1 overflow-x-auto pb-2 lg:pb-0 lg:overflow-visible scrollbar-thin">
                                {categoryList.map((type) => (
                                    <li key={type}>
                                        <button
                                            onClick={() => onCategoryClick(type)}
                                            className="w-full lg:w-full text-left px-4 py-2.5 rounded-lg text-[#704e37] font-medium hover:bg-[#704e37]/10 transition whitespace-nowrap lg:whitespace-normal"
                                        >
                                            {categoryLabels[type]}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    </aside>

                    {/* Main: Products */}
                    <main className="flex-1 min-w-0">
                        <section>
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-semibold text-gray-800">Veshje</h2>
                                {(appliedSearchName || hasActiveFilters) && displayList.length > 0 && (
                                    <span className="text-sm text-gray-500">
                                        {displayList.length} rezultat{displayList.length !== 1 ? "e" : ""}
                                    </span>
                                )}
                            </div>
                            {isLoading && <div className="flex justify-center py-16"><LoadingView size={50} /></div>}
                            {error && (
                                <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">
                                    {error}
                                </div>
                            )}
                            {!isLoading && !error && displayList.length > 0 && (
                                <>
                                    <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                                        {displayList.map((item) => (
                                                <ProductCard
                                                    key={`${item.seller_id}_${item.id}`}
                                                    item={item}
                                                    isFavorite={FavoritesActions.isFavorite(item, favorites)}
                                                    onToggleFavorite={onToggleFavorite}
                                                    onClick={onItemClick}
                                                    currentUserId={user?.id}
                                                />
                                            ))}
                                    </div>
                                </>
                            )}
                            {!isLoading && !error && displayList.length === 0 && (
                                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12 text-center">
                                    <p className="text-gray-600 mb-1">Nuk u gjet asnjë produkt.</p>
                                    <p className="text-gray-400 text-sm">Ndryshoni kërkimin ose filtrat.</p>
                                </div>
                            )}
                        </section>
                    </main>
                </div>
            </div>

            <ClothesFiltersModal
                visible={filtersModalOpen}
                filters={filters}
                onConfirm={applyFiltersAndFetch}
                onCancel={() => setFiltersModalOpen(false)}
            />
            <MobileAppDownloadBar onOpenChange={setMobileAppBarOpen} />
        </div>
    );
};

export default CatalogHome;
